import { clsx } from 'clsx'
import { type FC } from 'react'

import { ErrorBoundary } from '../../../../error-boundary'
import { useExperimentsContext } from '../../../../experiments/components/ExperimentsContext'
import { useDetectScrollUpForHeaderStickyToggleExperiment } from '../../../../experiments/header-sticky-toggle-experiment/hooks/useDetectScrollUpForHeaderStickyToggleExperiment'
import { useGetExperimentForTenant } from '../../../../experiments/hooks'
import { EXPERIMENT_HEADER_STICKY_TOGGLE } from '../../../../experiments/model/EXPERIMENT_NAME_PER_TENANT'
import { EXPERIMENT_VARIATION } from '../../../../experiments/model/EXPERIMENT_VARIATION'
import {
  experimentAccessorIsEnabled,
  experimentAccessorIsEnabledAndVariant,
} from '../../../../experiments/model/experimentAccessor'
import { HeaderSearchDesktop } from '../../header-search/components/HeaderSearchDesktop'
import { useHeaderDataContext } from '../../headerConfigContext'

import { HeaderMiddleSectionBrandLogo } from './HeaderMiddleSectionBrandLogo'
import { HeaderMiddleSectionIconsBox } from './HeaderMiddleSectionIconsBox'
import { HeaderMiddleSectionUsp } from './HeaderMiddleSectionUsp'

import styles from './HeaderMiddleSection.module.css'

interface HeaderMiddleSectionProps {
  isSticky: boolean
}

export const HeaderMiddleSection: FC<HeaderMiddleSectionProps> = ({
  isSticky,
}) => {
  const { shopLogos } = useHeaderDataContext()
  const { experiments } = useExperimentsContext()
  const headerStickyToggleExperiment = useGetExperimentForTenant(
    EXPERIMENT_HEADER_STICKY_TOGGLE,
  )

  const isScrollingUp = useDetectScrollUpForHeaderStickyToggleExperiment(
    headerStickyToggleExperiment,
  )
  const defaultHeaderStickyConditions =
    !experimentAccessorIsEnabled(
      headerStickyToggleExperiment.name,
      experiments,
    ) ||
    experimentAccessorIsEnabledAndVariant(
      headerStickyToggleExperiment,
      EXPERIMENT_VARIATION.DEFAULT,
    )

  const headerClassName = clsx(
    styles['header-middle-section'],
    'top-0 z-30 hidden bg-light-primary-low tablet:block',
    defaultHeaderStickyConditions && 'sticky',
    experimentAccessorIsEnabledAndVariant(
      headerStickyToggleExperiment,
      EXPERIMENT_VARIATION.V2,
    ) && 'sticky',
    experimentAccessorIsEnabledAndVariant(
      headerStickyToggleExperiment,
      EXPERIMENT_VARIATION.V3,
    ) &&
      isScrollingUp &&
      'sticky',
    isSticky && 'shadow-m',
  )

  return (
    <header className={headerClassName}>
      <div className="container relative flex py-2">
        <HeaderMiddleSectionBrandLogo
          className="relative mr-5 flex justify-center desktop:basis-48"
          isSticky={isSticky}
          shopLogos={shopLogos}
        />
        <div className="flex min-h-[56px] grow">
          <ErrorBoundary>
            <HeaderSearchDesktop className="relative mr-5 flex grow items-center" />
          </ErrorBoundary>
          <HeaderMiddleSectionUsp />
          <HeaderMiddleSectionIconsBox />
        </div>
      </div>
    </header>
  )
}
